import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { ShieldCheck, Plus, Loader2, CheckCircle2, XCircle, AlertTriangle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_COLORS = {
  compliant: 'bg-emerald-100 text-emerald-700',
  non_compliant: 'bg-red-100 text-red-700',
  in_progress: 'bg-amber-100 text-amber-700',
  not_applicable: 'bg-slate-100 text-slate-500',
  unknown: 'bg-slate-50 text-slate-400',
};

export default function ComplianceTracker({ token }) {
  const [data, setData] = useState(null);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ property_id: '', platform_type_id: '', framework_id: '', status: 'unknown', verified_date: '', notes: '' });
  const [properties, setProperties] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [r, f, props, plats] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reports/compliance-matrix`, { headers }),
        axios.get(`${BACKEND_URL}/api/compliance/frameworks`),
        axios.get(`${BACKEND_URL}/api/properties`, { headers }),
        axios.get(`${BACKEND_URL}/api/platform-types`, { headers }),
      ]);
      setData(r.data);
      setFrameworks(f.data || []);
      setProperties(props.data || []);
      setPlatforms(plats.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/compliance`, form, { headers });
      toast.success('Compliance record saved');
      setAddOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const s = data?.summary || {};

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="compliance-tracker">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Compliance & Audit</h2>
          <p className="text-sm text-muted-foreground">Track compliance status per property and platform across {frameworks.length} frameworks.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} className="gap-1 text-xs" data-testid="add-compliance-btn"><Plus className="h-3 w-3" /> Add Record</Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk', color: s.compliance_rate >= 70 ? '#16a34a' : '#dc2626' }}>{s.compliance_rate || 0}%</div>
          <div className="text-[10px] text-muted-foreground">Compliance Rate</div>
        </div>
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Space Grotesk' }}>{s.compliant || 0}</div>
          <div className="text-[10px] text-muted-foreground">Compliant</div>
        </div>
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Space Grotesk' }}>{s.non_compliant || 0}</div>
          <div className="text-[10px] text-muted-foreground">Non-Compliant</div>
        </div>
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{s.total_records || 0}</div>
          <div className="text-[10px] text-muted-foreground">Total Records</div>
        </div>
      </div>

      {/* Property table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm" id="report-content">
          <thead><tr className="bg-muted/50">
            <th className="p-2.5 text-left text-xs font-semibold">Property</th>
            <th className="p-2.5 text-center text-xs font-semibold">Records</th>
            <th className="p-2.5 text-center text-xs font-semibold text-emerald-700">Compliant</th>
            <th className="p-2.5 text-center text-xs font-semibold text-red-700">Non-Compliant</th>
            <th className="p-2.5 text-center text-xs font-semibold text-amber-700">In Progress</th>
            <th className="p-2.5 text-center text-xs font-semibold">Score</th>
            <th className="p-2.5 text-left text-xs font-semibold">Frameworks</th>
          </tr></thead>
          <tbody>
            {(data?.properties || []).filter(p => p.total_records > 0).map((p, i) => (
              <tr key={i} className="border-t hover:bg-muted/20">
                <td className="p-2.5 font-medium">{p.property_name}</td>
                <td className="p-2.5 text-center font-mono text-xs">{p.total_records}</td>
                <td className="p-2.5 text-center text-emerald-600 font-mono text-xs">{p.compliant}</td>
                <td className="p-2.5 text-center text-red-600 font-mono text-xs">{p.non_compliant}</td>
                <td className="p-2.5 text-center text-amber-600 font-mono text-xs">{p.in_progress}</td>
                <td className="p-2.5 text-center">
                  <span className={`text-xs font-bold ${p.compliance_pct >= 70 ? 'text-emerald-600' : p.compliance_pct >= 40 ? 'text-amber-600' : 'text-red-600'}`}>{p.compliance_pct}%</span>
                </td>
                <td className="p-2.5"><div className="flex flex-wrap gap-1">{(p.frameworks || []).map(f => <Badge key={f} variant="outline" className="text-[8px]">{f}</Badge>)}</div></td>
              </tr>
            ))}
            {(data?.properties || []).filter(p => p.total_records > 0).length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">No compliance records yet. Add your first record above.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Compliance Record</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-sm font-medium">Property</label>
              <Select value={form.property_id} onValueChange={v => setForm(p => ({ ...p, property_id: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{properties.filter(p => p.id !== 'corporate').map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium">Platform</label>
              <Select value={form.platform_type_id} onValueChange={v => setForm(p => ({ ...p, platform_type_id: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium">Framework</label>
              <Select value={form.framework_id} onValueChange={v => setForm(p => ({ ...p, framework_id: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{frameworks.map(f => <SelectItem key={f.id} value={f.id}>{f.name} — {f.category}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium">Status</label>
              <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="compliant">Compliant</SelectItem>
                  <SelectItem value="non_compliant">Non-Compliant</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="not_applicable">Not Applicable</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium">Notes</label><Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Audit completed March 2026" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.property_id || !form.platform_type_id || !form.framework_id}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Save Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
